import AppCard from '@nxsys/components/AppCard';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelect from '@nxsys/components/AppSelect';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { formatMoneyNoDecimal, showCurrencyByCountry } from '@nxsys/utils';
import { getTaxYearRateThresholdOptions } from '@nxsys/utils/taxYear';
import { useState } from 'react';
import { useSelector } from 'react-redux';

const RatesThreshold = () => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const taxYearRateThresholdOptions = getTaxYearRateThresholdOptions();
  const [selectedTaxYear, setSelectedTaxYear] = useState<Option<string>>(
    taxYearRateThresholdOptions[0],
  );

  const dataEarnings = [
    {
      type: messages['ratesThreshold.lowerLevelQualifyingEarnings'],
      annual: 6240,
      weekly: 120,
      '2weekly': 240,
      '4weekly': 480,
      monthly: 520,
    },
    {
      type: messages['ratesThreshold.earningsTriggerAutomaticEnrolment'],
      annual: 10000,
      weekly: 192,
      '2weekly': 384,
      '4weekly': 768,
      monthly: 883,
    },
    {
      type: messages['ratesThreshold.upperLevelQualifyingEarnings'],
      annual: 50270,
      weekly: 967,
      '2weekly': 1934,
      '4weekly': 3867,
      monthly: 4189,
    },
  ];

  const columnsEarning = [
    {
      title: `${messages['common.payReferencePeriod']} ${selectedTaxYear.value}`,
      dataIndex: 'type',
      key: 'type',
      width: 300,
    },
    {
      title: messages['common.annual'],
      dataIndex: 'annual',
      key: 'annual',
      render: (value: number) =>
        formatMoneyNoDecimal(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['common.weekly'],
      dataIndex: 'weekly',
      key: 'weekly',
      render: (value: number) =>
        formatMoneyNoDecimal(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['common.twoWeekly'],
      dataIndex: '2weekly',
      key: '2weekly',
      render: (value: number) =>
        formatMoneyNoDecimal(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['common.fourWeekly'],
      dataIndex: '4weekly',
      key: '4weekly',
      render: (value: number) =>
        formatMoneyNoDecimal(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['common.monthly'],
      dataIndex: 'monthly',
      key: 'monthly',
      render: (value: number) =>
        formatMoneyNoDecimal(value, showCurrencyByCountry(payrollCountry)),
    },
  ];

  const columnContributions = [
    {
      title: messages['common.date'],
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: messages['ratesThreshold.employerMinimumContribution'],
      dataIndex: 'employerMinimum',
      date: 'employerMinimum',
    },
    {
      title: messages['ratesThreshold.totalMinimumContribution'],
      dataIndex: 'total',
      date: 'total',
    },
  ];

  const dataContributions = [
    {
      date: messages['ratesThreshold.currentRates'],
      employerMinimum: messages['ratesThreshold.employerMinimumPercent'],
      total: messages['ratesThreshold.totalMinimumPercent'],
    },
  ];

  return (
    <div className="RatesThreshold">
      <AppCard
        title={
          <div className="flex items-center justify-between">
            <div>{messages['common.earningsThresholds']}</div>
            <div>
              <AppSelect
                options={taxYearRateThresholdOptions}
                onChange={(value: Option<string>) => setSelectedTaxYear(value)}
                defaultValue={taxYearRateThresholdOptions[0]}
              />
            </div>
          </div>
        }
        lineTitleColor="#FFE785"
      >
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            columns={columnsEarning}
            data={dataEarnings}
            loading={false}
            pagination={false}
            page={1}
            total={dataEarnings.length}
            pageSize={dataEarnings.length}
            scroll={{ x: 1000 }}
          />
        </AppsContent>
      </AppCard>
      <AppCard
        title={
          <div className="flex items-center justify-between">
            <div>{messages['common.minimumContributions']}</div>
            <div>
              <AppSelect
                options={taxYearRateThresholdOptions}
                onChange={(value: Option<string>) => setSelectedTaxYear(value)}
                defaultValue={taxYearRateThresholdOptions[0]}
              />
            </div>
          </div>
        }
        lineTitleColor="#FFE785"
      >
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            columns={columnContributions}
            data={dataContributions}
            loading={false}
            pagination={false}
            page={1}
            total={dataContributions.length}
            pageSize={dataContributions.length}
            scroll={{ x: 1000 }}
          />
        </AppsContent>
      </AppCard>
    </div>
  );
};

export default RatesThreshold;
